<template>
    <div class="container-fluid marketplace-ledger">
        <div class="row">
            <div class="col-lg-12">
                <div class="card mb-3">
                    <div class="card-body">
                        <div class="d-flex flex-wrap align-items-center justify-content-between">
                            <div class="d-flex flex-wrap align-items-baseline mb-2">
                                <h4 class="mr-3 mb-0 text-nowrap">판매자배송 판매장부</h4>
                                <small class="text-muted">쿠팡 Open API 매출 인식 내역 기준 · {{ vendorId || suppliercode || '판매자코드 없음' }}</small>
                            </div>
                            <div class="d-flex flex-wrap align-items-center mb-2" v-if="vendorIds.length">
                                <b-form-select v-if="vendorIds.length > 1" v-model="vendorId" :options="vendorIds" size="sm" class="mr-2 mb-1 marketplace-ledger__vendor" @change="search" />
                                <b-form-input v-model="startDate" type="date" size="sm" class="mr-1 mb-1 marketplace-ledger__date" @change="search" />
                                <span class="mx-1 mb-1">~</span>
                                <b-form-input v-model="endDate" type="date" size="sm" class="mr-2 mb-1 marketplace-ledger__date" @change="search" />
                                <b-button variant="primary" size="sm" class="mr-1 mb-1" :disabled="busy" @click="search">
                                    <i class="fas fa-search mr-1"></i> 조회
                                </b-button>
                                <b-button variant="primary" size="sm" class="mb-1" :disabled="busy" @click="collect">
                                    <i class="fas fa-download mr-1"></i> 매출 모으기
                                </b-button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 키·확장 상태 -->
            <div class="col-lg-12" v-if="keyState === 'noExtension'">
                <b-alert show variant="warning">
                    쿠플러스 확장 또는 이 PC 의 쿠플러스 연결 프로그램이 응답하지 않습니다. 확장을 켜고 페이지를 새로고침해 주세요.
                </b-alert>
            </div>
            <div class="col-lg-12" v-else-if="keyState === 'noKey'">
                <div class="card">
                    <div class="card-body text-center py-5">
                        <h5 class="mb-2">Open API 키를 넣어 주세요</h5>
                        <p class="text-muted mb-3">
                            판매자배송 매출은 쿠팡 Open API 로 받습니다. 키는 이 PC 에만 저장되고 서버로 가지 않습니다.
                        </p>
                        <router-link :to="{ name: 'dashboard.coupangAccounts' }" class="btn btn-primary btn-sm">
                            멀티계정관리 › Open API 설정 열기
                        </router-link>
                    </div>
                </div>
            </div>

            <div class="col-lg-12" v-if="busy">
                <div class="text-center text-muted py-3">{{ busyText }}</div>
            </div>
            <div class="col-lg-12" v-if="errorText">
                <b-alert show variant="danger" dismissible @dismissed="errorText = ''">{{ errorText }}</b-alert>
            </div>
            <div class="col-lg-12" v-if="noticeText">
                <b-alert show variant="info" dismissible @dismissed="noticeText = ''">{{ noticeText }}</b-alert>
            </div>

            <template v-if="ledger">
                <div class="col-lg-12">
                    <div class="card mb-3">
                        <div class="card-body py-3">
                            <div class="row text-center">
                                <div class="col-6 col-md-3 mb-2">
                                    <div class="text-muted small">판매액</div>
                                    <strong class="marketplace-ledger__num" :class="ledger.summary.collectedDays ? '' : 'text-warning'">{{ sumText(won(ledger.summary.saleAmount)) }}</strong>
                                </div>
                                <div class="col-6 col-md-3 mb-2">
                                    <div class="text-muted small">정산액</div>
                                    <strong class="marketplace-ledger__num" :class="ledger.summary.collectedDays ? '' : 'text-warning'">{{ sumText(won(ledger.summary.settlementAmount)) }}</strong>
                                </div>
                                <div class="col-6 col-md-3 mb-2">
                                    <div class="text-muted small">수량</div>
                                    <strong class="marketplace-ledger__num" :class="ledger.summary.collectedDays ? '' : 'text-warning'">{{ sumText(count(ledger.summary.quantity)) }}</strong>
                                </div>
                                <div class="col-6 col-md-3 mb-2">
                                    <div class="text-muted small">모은 날 / 안 모은 날</div>
                                    <strong class="marketplace-ledger__num">{{ ledger.summary.collectedDays }} / </strong>
                                    <strong class="marketplace-ledger__num" :class="ledger.summary.missingDays ? 'text-warning' : ''">{{ ledger.summary.missingDays }}</strong>
                                </div>
                            </div>
                            <div v-if="ledger.summary.collectedDays === 0" class="text-center text-warning small mt-2">
                                이 기간은 아직 매출을 모으지 않았습니다 — «매출 모으기» 를 눌러 주세요.
                            </div>
                        </div>
                    </div>
                </div>

                <div class="col-lg-12">
                    <b-tabs content-class="mt-3" small>
                        <b-tab title="날짜별" active>
                            <b-table :items="ledger.days" :fields="dayFields" small responsive striped>
                                <template #cell(saleAmount)="d"><span :class="d.item.collected ? '' : 'text-warning'">{{ d.item.collected ? won(d.value) : '안 모음' }}</span></template>
                                <template #cell(settlementAmount)="d">{{ d.item.collected ? won(d.value) : '' }}</template>
                                <template #cell(quantity)="d">{{ d.item.collected ? count(d.value) : '' }}</template>
                                <template #cell(orders)="d">{{ d.item.collected ? count(d.value) : '' }}</template>
                            </b-table>
                        </b-tab>
                        <b-tab title="옵션별">
                            <b-table :items="ledger.items" :fields="itemFields" small responsive striped show-empty empty-text="이 기간 매출 줄이 없습니다.">
                                <template #cell(saleAmount)="d">{{ won(d.value) }}</template>
                                <template #cell(settlementAmount)="d">{{ won(d.value) }}</template>
                                <template #cell(quantity)="d">{{ count(d.value) }}</template>
                            </b-table>
                        </b-tab>
                    </b-tabs>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import axios from 'axios'
import { loadOpenApiKeys, fetchOpenApiRevenue } from '@/services/coupangAccountFlow'
import { buildMarketplaceLedger, splitRange, collectableRange } from '@/utils/marketplaceLedger'

// Open API 매출 인식 내역은 한 번에 31일까지 받는다
const REVENUE_MAX_DAYS = 31

function ymd(d) {
    const p = (n) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

export default {
    name: 'MarketplaceSalesLedger',
    data() {
        // 기본 기간 = 어제까지 7일(쿠팡 매출 내역은 어제까지만 나온다)
        const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000)
        const weekAgo = new Date(yesterday.getTime() - 6 * 24 * 60 * 60 * 1000)
        return {
            suppliercode: this.$store.state.user.suppliercode || '',   // 키 읽기 전·키 없을 때 머리 계정ID(다른 두 화면과 같은 값)
            keyState: 'loading',   // loading | noExtension | noKey | ready
            vendorIds: [],
            vendorId: '',
            startDate: ymd(weekAgo),
            endDate: ymd(yesterday),
            busy: false,
            busyText: '',
            errorText: '',
            noticeText: '',
            ledger: null,
            dayFields: [
                { key: 'date', label: '인식일' },
                { key: 'saleAmount', label: '판매액', class: 'text-right' },
                { key: 'settlementAmount', label: '정산액', class: 'text-right' },
                { key: 'quantity', label: '수량', class: 'text-right' },
                { key: 'orders', label: '주문', class: 'text-right' },
            ],
            itemFields: [
                { key: 'vendorItemId', label: '옵션ID' },
                { key: 'vendorItemName', label: '옵션명' },
                { key: 'saleAmount', label: '판매액', class: 'text-right' },
                { key: 'settlementAmount', label: '정산액', class: 'text-right' },
                { key: 'quantity', label: '수량', class: 'text-right' },
            ],
        }
    },
    async mounted() {
        try {
            const keys = await loadOpenApiKeys()
            if (!keys) { this.keyState = 'noExtension'; return }
            this.vendorIds = Object.keys(keys).filter((v) => keys[v].registered !== false).sort()
            if (!this.vendorIds.length) { this.keyState = 'noKey'; return }
            this.vendorId = this.vendorIds[0]
            this.keyState = 'ready'
            await this.search()
        } catch (e) {
            console.error('[판매자배송 판매장부] 키 목록 실패:', e)
            this.errorText = '이 PC 의 Open API 키 목록을 읽지 못했습니다.'
        }
    },
    methods: {
        won(v) { return v === null || v === undefined ? '' : Math.round(v).toLocaleString() + '원' },
        count(v) { return v === null || v === undefined ? '' : Number(v).toLocaleString() },
        // 합 칸 — 그 기간 모은 날이 0 이면 빈칸 대신 «안 모음»
        sumText(t) { return t === '' ? '안 모음' : t },
        rangeOk() {
            if (!this.startDate || !this.endDate || this.startDate > this.endDate) {
                this.errorText = '조회 기간을 확인해 주세요.'
                return false
            }
            return true
        },
        async search() {
            if (!this.vendorId || !this.rangeOk()) return
            this.busy = true
            this.busyText = '판매장부 조회 중...'
            this.errorText = ''
            try {
                const r = await axios.post('/api/marketplace/listSales', { vendorId: this.vendorId, start: this.startDate, end: this.endDate })
                if (!r.data || !r.data.success) throw new Error((r.data && r.data.message) || '조회 실패')
                this.ledger = buildMarketplaceLedger(r.data.sales, r.data.collectedDates, this.startDate, this.endDate)
            } catch (e) {
                console.error('[판매자배송 판매장부] 조회 실패:', e)
                this.errorText = '판매장부를 불러오지 못했습니다: ' + ((e.response && e.response.data && e.response.data.message) || e.message)
            } finally {
                this.busy = false
            }
        },
        async collect() {
            if (!this.vendorId || !this.rangeOk()) return
            // 쿠팡 매출 내역은 어제까지만 — 오늘이 든 기간은 끝날을 어제로 자르고 알린다
            const range = collectableRange(this.startDate, this.endDate, ymd(new Date()))
            if (!range) {
                this.errorText = ''
                this.noticeText = '오늘 매출은 쿠팡이 내일부터 내줍니다. 어제까지 기간을 골라 주세요.'
                return
            }
            this.busy = true
            this.errorText = ''
            this.noticeText = range.cut ? `오늘 매출은 내일부터 받을 수 있어 ${range.to} 까지 모읍니다.` : ''
            try {
                const parts = splitRange(range.from, range.to, REVENUE_MAX_DAYS)
                for (let i = 0; i < parts.length; i++) {
                    const [from, to] = parts[i]
                    this.busyText = `쿠팡에서 매출 받는 중... (${i + 1}/${parts.length} · ${from}~${to})`
                    const got = await fetchOpenApiRevenue(this.vendorId, from, to)
                    if (got.needKey) { this.keyState = 'noKey'; return }
                    if (!got.ok) throw new Error(got.message || got.error || '쿠팡 매출을 받지 못했습니다.')
                    this.busyText = `서버에 저장 중... (${i + 1}/${parts.length})`
                    const saved = await axios.post('/api/marketplace/saveSales', { vendorId: this.vendorId, from, to, rows: got.rows })
                    if (!saved.data || !saved.data.success) throw new Error((saved.data && saved.data.message) || '저장 실패')
                }
                await this.search()
            } catch (e) {
                console.error('[판매자배송 판매장부] 모으기 실패:', e)
                this.errorText = '매출을 모으지 못했습니다: ' + ((e.response && e.response.data && e.response.data.message) || e.message)
            } finally {
                this.busy = false
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.marketplace-ledger {
    &__vendor { width: 140px; }
    &__date { width: 140px; }
    &__num { font-size: 18px; }
    // 390 — 날짜·«원» 이 칸 안에서 꺾이지 않게(좁으면 표가 옆으로 밀림)
    ::v-deep th, ::v-deep td { white-space: nowrap; }
    // 닫기 × 자리 — 테마가 .alert-dismissible 오른쪽 여백을 꺼 둬서 390 에서 × 가 글을 덮음 · 낱말 가운데 꺾임도 막음
    ::v-deep .alert-dismissible { padding-right: 3rem; word-break: keep-all; }
    // 테마가 767px 아래 .table 을 block 으로 바꿔 줄이 카드 폭을 못 채움 — 옆 넘침은 .table-responsive 가 맡으므로 표로 되돌림
    ::v-deep .table-responsive > .table { display: table; }
    @media (max-width: 575.98px) { ::v-deep .table tr > * { font-size: 13px; padding-left: .4rem !important; padding-right: .4rem !important; } }   // 테마 칸 여백 20px 이 다섯 칸을 밀어냄
}
</style>
